/*!
 * Checklist Analytics Tracker
 * -----------------------------------------------------------------------------
 * Tiny client-side library that ships analytics events to the backend at
 *   https://app.motsesim.co.il/api/analytics/events
 *
 * Design goals:
 *   • Zero UI impact  — uses navigator.sendBeacon (async, non-blocking).
 *   • No data loss   — falls back to fetch(keepalive) → fetch → localStorage retry.
 *   • Hybrid delivery — user actions fire immediately, high-frequency events
 *                       (impressions) are batched every 3s.
 *   • De-dup safe    — each event carries a UUID; server ignores duplicates.
 *
 * Public API (attached to window.ChecklistAnalytics):
 *   .track(eventName, properties)          → fire immediately (beacon)
 *   .trackBatched(eventName, properties)    → queued, flushed every ~3s
 *   .flush()                                → force-flush the batch queue
 *   .identify(customerId)                   → attach a customer id to future events
 *   .getSessionId()                         → current session id
 *   .setEndpoint(url)                       → override endpoint (tests/staging)
 * -----------------------------------------------------------------------------
 */
(function (global) {
  "use strict";

  var DEFAULT_ENDPOINT = "https://app.motsesim.co.il/api/analytics/events";
  var SHOP_ID = "motsesim.myshopify.com";
  var BATCH_INTERVAL_MS = 3000;
  var MAX_BATCH_SIZE = 20;
  var RETRY_STORAGE_KEY = "cla_retry_queue_v1";
  var SESSION_STORAGE_KEY = "cla_session_id";
  var CUSTOMER_STORAGE_KEY = "cla_customer_id";
  var TRAITS_STORAGE_KEY = "cla_user_traits";
  var MAX_RETRY_QUEUE = 200;

  // Case-insensitive tag substrings that force a gift_giver classification
  // regardless of URL. Extend once we know the exact tag values in Shopify.
  var GIFT_GIVER_TAG_HINTS = [
    "family_friends",
    "gift-giver",
    "gift_giver",
    "friends-and-family",
    "friends_and_family",
    "f_and_f",
    "f&f",
    "קרובי משפחה", // Hebrew: "family & friends" prefix
  ];

  // Case-insensitive substrings that force a parent classification.
  var PARENT_TAG_HINTS = [
    "expecting",
    "fresh_parents",
    "fresh-parents",
    "new_parent",
    "new-parent",
    "מצפים לתינוק",
    "הורים טריים",
  ];

  // -------------------- helpers ---------------------------------------------

  function uuid() {
    if (global.crypto && global.crypto.randomUUID) {
      try { return global.crypto.randomUUID(); } catch (e) {}
    }
    // RFC4122 v4 fallback
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function detectDeviceType() {
    var w = global.innerWidth || 1024;
    if (w < 640) return "mobile";
    if (w < 1024) return "tablet";
    return "desktop";
  }

  function readSessionId() {
    try {
      var s = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (s) return s;
      s = uuid();
      sessionStorage.setItem(SESSION_STORAGE_KEY, s);
      return s;
    } catch (e) {
      return "no-storage-" + uuid();
    }
  }

  function readCustomerId() {
    try {
      return sessionStorage.getItem(CUSTOMER_STORAGE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function writeCustomerId(id) {
    try {
      if (id) sessionStorage.setItem(CUSTOMER_STORAGE_KEY, String(id));
      else sessionStorage.removeItem(CUSTOMER_STORAGE_KEY);
    } catch (e) {}
  }

  function readUserTraits() {
    try {
      var raw = sessionStorage.getItem(TRAITS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeUserTraits(traits) {
    try {
      if (traits && Object.keys(traits).length) {
        sessionStorage.setItem(TRAITS_STORAGE_KEY, JSON.stringify(traits));
      } else {
        sessionStorage.removeItem(TRAITS_STORAGE_KEY);
      }
    } catch (e) {}
  }

  function tagMatchesAny(tags, hints) {
    if (!Array.isArray(tags) || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || "").toLowerCase();
      for (var j = 0; j < hints.length; j++) {
        if (t.indexOf(String(hints[j]).toLowerCase()) !== -1) return true;
      }
    }
    return false;
  }

  function loadRetryQueue() {
    try {
      var raw = localStorage.getItem(RETRY_STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveRetryQueue(list) {
    try {
      if (!list || !list.length) {
        localStorage.removeItem(RETRY_STORAGE_KEY);
        return;
      }
      // trim to newest N to prevent unbounded growth
      var trimmed = list.slice(-MAX_RETRY_QUEUE);
      localStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify(trimmed));
    } catch (e) {}
  }

  // -------------------- tracker instance ------------------------------------

  function createTracker() {
    var endpoint = DEFAULT_ENDPOINT;
    var sessionId = readSessionId();
    var customerId = readCustomerId();
    var userTraits = readUserTraits(); // { tags: [...], family_status: "...", first_name: ... }
    var batchQueue = [];
    var batchTimer = null;

    // Classify each event as "parent" (target segment = expecting parents /
    // new parents on their own funnel) vs "gift_giver" (F&F browsing a
    // shared checklist). Business rule: dashboards report the parent
    // segment as 100%; F&F is excluded from the primary funnel.
    //
    // Signals used (in order of certainty — strongest first):
    //   1. Customer trait `family_status`:
    //        "family_friends" / "קרובי משפחה וחברים"           → gift_giver
    //        "expecting" / "מצפים לתינוק"                       → parent
    //        "fresh_parents" / "הורים טריים"                    → parent
    //   2. Shopify customer tags matching known F&F / parent hints
    //   3. F&F-only events (mark_as_purchased, gift_note_*, shared_*) → gift_giver
    //   4. URL path starts with /pages/checklist-share      → gift_giver
    //   5. URL query has ?type=checklist-<id> where id !== customerId → gift_giver
    //   6. Everything else                                  → parent
    function classifyUserSegment(eventName) {
      try {
        var fs = String((userTraits && userTraits.family_status) || "").toLowerCase();
        if (
          fs === "family_friends" ||
          fs.indexOf("קרובי משפחה") !== -1 ||
          fs.indexOf("family_friends") !== -1
        ) {
          return "gift_giver";
        }
        if (
          fs === "expecting" ||
          fs === "fresh_parents" ||
          fs.indexOf("מצפים לתינוק") !== -1 ||
          fs.indexOf("הורים טריים") !== -1
        ) {
          return "parent";
        }

        var tags = (userTraits && userTraits.tags) || [];
        if (tagMatchesAny(tags, GIFT_GIVER_TAG_HINTS)) return "gift_giver";
        if (tagMatchesAny(tags, PARENT_TAG_HINTS)) return "parent";

        if (
          eventName === "mark_as_purchased_clicked" ||
          eventName === "gift_note_submitted" ||
          eventName === "shared_checklist_opened" ||
          eventName === "gift_purchase_for_checklist_owner"
        ) {
          return "gift_giver";
        }

        var loc = global.location || {};
        var path = (loc.pathname || "").toLowerCase();
        if (path.indexOf("/pages/checklist-share") === 0) return "gift_giver";

        var params = null;
        try { params = new URLSearchParams(loc.search || ""); } catch (e) {}
        var typeParam = params ? params.get("type") : null;
        if (typeParam && typeParam.indexOf("checklist-") === 0) {
          var ownerId = typeParam.replace("checklist-", "");
          if (!customerId) return "gift_giver";
          if (ownerId && String(ownerId) !== String(customerId)) return "gift_giver";
        }

        return "parent";
      } catch (e) {
        return "parent";
      }
    }

    function buildEvent(name, props) {
      var traitsForEvent = {};
      if (userTraits && userTraits.family_status) traitsForEvent.family_status = userTraits.family_status;
      if (userTraits && Array.isArray(userTraits.tags) && userTraits.tags.length) {
        traitsForEvent.user_tags = userTraits.tags;
      }
      return {
        event_id: uuid(),
        event_name: name,
        session_id: sessionId,
        customer_id: customerId,
        occurred_at: Date.now(),
        properties: Object.assign(
          {
            device_type: detectDeviceType(),
            page_path: global.location && global.location.pathname,
            page_url: global.location && global.location.href,
            referrer: (global.document && global.document.referrer) || "",
            user_segment: classifyUserSegment(name),
          },
          traitsForEvent,
          props || {},
        ),
      };
    }

    function serializePayload(events) {
      return JSON.stringify({ events: events });
    }

    // NOTE: We deliberately use Content-Type: text/plain to keep this a
    // "simple" CORS request (no preflight). The server reads the raw body
    // and JSON.parses it, so the on-the-wire content is still JSON — only
    // the header lies. Custom headers like X-Shop-Id are avoided for the
    // same reason; the shop is passed as a ?shop= query param instead.
    function beaconSend(events) {
      if (!global.navigator || !global.navigator.sendBeacon) return false;
      try {
        var blob = new Blob([serializePayload(events)], { type: "text/plain" });
        return global.navigator.sendBeacon(endpoint + "?shop=" + encodeURIComponent(SHOP_ID), blob);
      } catch (e) {
        return false;
      }
    }

    function fetchSend(events, keepalive) {
      if (!global.fetch) return Promise.resolve(false);
      return global
        .fetch(endpoint + "?shop=" + encodeURIComponent(SHOP_ID), {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
          body: serializePayload(events),
          keepalive: !!keepalive,
          mode: "cors",
          credentials: "omit",
        })
        .then(function (res) { return res && res.ok; })
        .catch(function () { return false; });
    }

    function queueForRetry(events) {
      if (!events || !events.length) return;
      var q = loadRetryQueue().concat(events);
      saveRetryQueue(q);
    }

    function drainRetryQueue() {
      var q = loadRetryQueue();
      if (!q.length) return;
      // Send in chunks of MAX_BATCH_SIZE
      var chunks = [];
      for (var i = 0; i < q.length; i += MAX_BATCH_SIZE) {
        chunks.push(q.slice(i, i + MAX_BATCH_SIZE));
      }
      saveRetryQueue([]);
      chunks.forEach(function (chunk) {
        fetchSend(chunk, false).then(function (ok) {
          if (!ok) queueForRetry(chunk);
        });
      });
    }

    function sendNow(events) {
      if (!events || !events.length) return;
      if (beaconSend(events)) return;
      fetchSend(events, true).then(function (ok) {
        if (!ok) queueForRetry(events);
      });
    }

    function scheduleFlush() {
      if (batchTimer != null) return;
      batchTimer = global.setTimeout(function () {
        batchTimer = null;
        flush();
      }, BATCH_INTERVAL_MS);
    }

    function flush() {
      if (batchTimer != null) {
        global.clearTimeout(batchTimer);
        batchTimer = null;
      }
      if (!batchQueue.length) return;
      var toSend = batchQueue.splice(0, batchQueue.length);
      sendNow(toSend);
    }

    // Public: fire an event immediately.
    function track(name, props) {
      try {
        if (!name) return;
        var evt = buildEvent(name, props);
        sendNow([evt]);
      } catch (e) {
        // swallow — analytics must never break the site
        if (global.console && global.console.debug) global.console.debug("[analytics] track error", e);
      }
    }

    // Public: enqueue for batched delivery.
    function trackBatched(name, props) {
      try {
        if (!name) return;
        batchQueue.push(buildEvent(name, props));
        if (batchQueue.length >= MAX_BATCH_SIZE) {
          flush();
        } else {
          scheduleFlush();
        }
      } catch (e) {
        if (global.console && global.console.debug) global.console.debug("[analytics] trackBatched error", e);
      }
    }

    // Accepts either a scalar customerId OR an object of traits:
    //   identify("123")
    //   identify({ customer_id: "123", tags: [...], family_status: "..." })
    // Object form persists traits for the session so every future event
    // includes them (used for segment classification + downstream queries).
    function identify(idOrTraits) {
      if (idOrTraits && typeof idOrTraits === "object") {
        var incoming = idOrTraits;
        var id = incoming.customer_id || incoming.id || null;
        if (id) {
          customerId = String(id);
          writeCustomerId(customerId);
        }
        var merged = Object.assign({}, userTraits || {});
        if (Array.isArray(incoming.tags)) merged.tags = incoming.tags;
        else if (typeof incoming.tags === "string" && incoming.tags) {
          merged.tags = incoming.tags.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
        }
        if (incoming.family_status) merged.family_status = String(incoming.family_status);
        if (incoming.first_name) merged.first_name = String(incoming.first_name);
        userTraits = merged;
        writeUserTraits(userTraits);
        return;
      }
      customerId = idOrTraits ? String(idOrTraits) : null;
      writeCustomerId(customerId);
    }

    // Public API
    var api = {
      track: track,
      trackBatched: trackBatched,
      flush: flush,
      identify: identify,
      getSessionId: function () { return sessionId; },
      getCustomerId: function () { return customerId; },
      setEndpoint: function (url) { if (url) endpoint = String(url); },
      getEndpoint: function () { return endpoint; },
      // exposed for tests / debug
      _sendNow: sendNow,
    };

    // Flush on page hide / unload so batched events aren't lost.
    if (global.addEventListener) {
      global.addEventListener("pagehide", flush);
      global.addEventListener("beforeunload", flush);
      global.addEventListener("visibilitychange", function () {
        if (global.document && global.document.visibilityState === "hidden") flush();
      });
      // Retry queued failures on page load.
      global.addEventListener("load", function () {
        try { drainRetryQueue(); } catch (e) {}
      });
    }

    // Best-effort auto-identify from Shopify globals (theme).
    try {
      var meta = global.__st || (global.Shopify && global.Shopify.customer) || null;
      if (meta) {
        var maybeId = meta.customer_id || (meta && meta.id);
        if (maybeId && !customerId) identify(maybeId);
      }
    } catch (e) {}

    return api;
  }

  if (!global.ChecklistAnalytics) {
    global.ChecklistAnalytics = createTracker();
  }
})(typeof window !== "undefined" ? window : this);
